import { useMemo } from 'react';
import { TourType } from '@/Components/Tour/Tour';
import { useLocalization } from '@/Hooks';
import { useDispatch, useSelector } from 'react-redux';
import { accountChartsDataSelectors } from '@/Redux/DynamicData/AccountChartsData/selectors';
import { accountChartsDataActions } from '@/Redux/DynamicData/AccountChartsData/reducer';

export function useAccountChartSettings() {
  const dispatch = useDispatch();

  const {
    tours: {
      AccountingSettings: { AccountChart, AccountChartModal },
    },
  } = useLocalization();

  const { accountChartType } = useSelector(accountChartsDataSelectors.accountChartsInfo);

  const pageTour = useMemo<TourType>(
    () => ({
      id: 'pageTour',
      title: AccountChart.Title,
      steps: [
        {
          content: AccountChart.Step1.Text,
          placement: 'auto',
          target: '#accountChart',
          title: AccountChart.Step1.Title,
          disableBeacon: true,
          link: AccountChart.Step1.DetailsLink,
        },
        {
          content: AccountChart.Step2.Text,
          placement: 'top',
          target: '#accountChartsTable',
          title: AccountChart.Step2.Title,
          disableBeacon: true,
          link: AccountChart.Step2.DetailsLink,
          floaterProps: {
            disableFlip: true,
          },
        },
        {
          content: AccountChart.Step3.Text,
          placement: 'auto',
          target: '#crudExtended',
          title: AccountChart.Step3.Title,
          disableBeacon: true,
          link: AccountChart.Step3.DetailsLink,
        },
        {
          content: AccountChart.Step4.Text,
          placement: 'auto',
          target: '.dx-toolbar-after',
          title: AccountChart.Step4.Title,
          disableBeacon: true,
          link: AccountChart.Step4.DetailsLink,
          spotlightPadding: 0,
          styles: {
            spotlight: {
              maxWidth: '500px',
            },
          },
        },
        {
          content: AccountChart.Step5.Text,
          placement: 'auto',
          target: '#tableImportButton',
          title: AccountChart.Step5.Title,
          disableBeacon: true,
          link: AccountChart.Step5.DetailsLink,
          styles: {
            spotlight: {
              minWidth: '65px',
            },
          },
        },
      ],
    }),
    [AccountChart],
  );

  const accountChartSettingsTour = useMemo<TourType>(
    () => ({
      id: 'accountChartSettingsTour',
      title: AccountChartModal.Title,
      before: () => {
        if (accountChartType) {
          dispatch(accountChartsDataActions.showAccountChartModal({ accountChartType: accountChartType }));
        }
      },
      steps: [
        {
          content: AccountChartModal.Step1.Text,
          placement: 'auto',
          target: '#modalTitle',
          title: AccountChartModal.Step1.Title,
          disableBeacon: true,
          link: AccountChartModal.Step1.DetailsLink,
        },
        {
          content: AccountChartModal.Step2.Text,
          placement: 'auto',
          target: '#accountChartForm',
          title: AccountChartModal.Step2.Title,
          disableBeacon: true,
          link: AccountChartModal.Step2.DetailsLink,
        },
        {
          content: AccountChartModal.Step3.Text,
          placement: 'auto',
          target: '#isActiveToggle',
          title: AccountChartModal.Step3.Title,
          disableBeacon: true,
          link: AccountChartModal.Step3.DetailsLink,
        },
        {
          content: AccountChartModal.Step4.Text,
          placement: 'auto',
          target: '#modalNavButtons',
          title: AccountChartModal.Step4.Title,
          disableBeacon: true,
          link: AccountChartModal.Step4.DetailsLink,
        },
      ],
    }),
    [AccountChartModal, accountChartType],
  );

  return [pageTour, accountChartSettingsTour];
}
